'use client';

import React, { FC, forwardRef, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useShallow } from 'zustand/react/shallow';

import { EditCurrenciesModal } from '../Deposit/components/CurrencyBlocks/EditCurrenciesModal';
import { Wallet } from '@/api/auth';
import { Button } from '@/components/buttons';
import { CurrencyItem } from '@/components/Deposit/components/CurrencyBlocks/CurrencyItem';
import { ModalSelect, ModalSelectContainer } from '@/components/ModalSelect';
import { ScrollBlock } from '@/components/ScrollBlock';
import {
  amountWithCurrencySymbol,
  getCurrencyName,
  mapCurrencyName,
} from '@/helpers/currency';
import { ThemeMode } from '@/helpers/enums';
import { useComponentVisible } from '@/hooks/visibility';
import { useCommonStore, useDepositStore } from '@/store';

import {
  ScOptionsWalletsList,
  ScSelectDeposit,
  ScSelectDepositProps,
} from './styled';
import { Spinner } from '@/styles/common';

type SelectDepositProps = Omit<ScSelectDepositProps, 'active'> & {
  noEditCurrencyBtn?: boolean;
  togglePreventValidation?: (arg: boolean) => void;
};

export const SelectDeposit: FC<SelectDepositProps> = ({
  isInputAddon,
  withoutSum,
  noEditCurrencyBtn,
  togglePreventValidation,
  ...props
}) => {
  const { t } = useTranslation('deposits');
  const { images, grayLightColor, mainColor, whiteColor } = useTheme();
  const [isEditCurrenciesOpen, setIsEditCurrenciesOpen] = useState(false);

  const [checkUserData, toggleCheckUserData] = useState(false);
  const setOriginalAmount = useDepositStore.use.setOriginalAmount();
  const {
    extendedUserWallets,
    currentWallet,
    changeUserWallet,
    pendingUser,
    pendingWallets,
    currencyConversion,
    isMobileDevice,
  } = useCommonStore(
    useShallow((state) => ({
      extendedUserWallets: state.extendedUserWallets,
      currentWallet: state.currentWallet,
      changeUserWallet: state.changeUserWallet,
      pendingUser: state.pendingUser,
      pendingWallets: state.pendingWallets,
      currencyConversion: state.currencyConversion,
      isMobileDevice: state.isMobileDevice,
    }))
  );

  const { ref, openerRef, isComponentVisible, setIsComponentVisible } =
    useComponentVisible(false);

  const pending = useMemo(
    () => pendingUser || pendingWallets,
    [pendingUser, pendingWallets]
  );

  const handleOpen = () => {
    setIsComponentVisible(!isComponentVisible);
  };

  const handleChange = async (currency: Wallet['currency']) => {
    handleOpen();
    await changeUserWallet(currency);
    setOriginalAmount('');
    toggleCheckUserData(true);
  };

  useEffect(() => {
    if (!pending && checkUserData) {
      toggleCheckUserData(false);
    }
  }, [pending, checkUserData, toggleCheckUserData]);

  const optionsWallets = useMemo(
    () =>
      extendedUserWallets.map(
        ({ amountCents, currency, subCurrency, icon }) => {
          const name = mapCurrencyName[currency];

          return {
            name: `${amountWithCurrencySymbol(getCurrencyName(subCurrency), amountCents)}${
              name ? ` | ${name}` : ''
            }`,
            value: currency,
            icon,
          };
        }
      ),
    [extendedUserWallets]
  );

  useEffect(() => {
    if (!togglePreventValidation) return;

    togglePreventValidation(isComponentVisible);
  }, [isComponentVisible, togglePreventValidation]);

  if (!currentWallet) return null;

  return (
    <ScSelectDeposit
      active={isComponentVisible}
      isInputAddon={isInputAddon}
      {...props}
    >
      <button
        ref={openerRef}
        className="select-button"
        onClick={handleOpen}
        disabled={pending}
        type="button"
      >
        {pending && !isInputAddon ? (
          <Spinner
            size={20}
            center
            color={mainColor}
            background={isInputAddon ? whiteColor : grayLightColor}
          />
        ) : (
          <>
            <div className="value">
              {withoutSum
                ? currencyConversion(
                    currentWallet.amountCents,
                    getCurrencyName(currentWallet.currency)
                  )
                : amountWithCurrencySymbol(
                    getCurrencyName(currentWallet.currency),
                    currentWallet.amountCents
                  )}
            </div>
            <div className="arrow">
              <images.arrowDownGrayIcon.component />
            </div>
          </>
        )}
      </button>
      {isComponentVisible &&
        (isMobileDevice ? (
          isInputAddon ? (
            <ModalSelectContainer
              title={t('editCurrency.myCurrencies')}
              onClose={handleOpen}
              icon={<images.currencyIcon.component />}
              innerAlignBottom
              openOptionsInPortal
            >
              <ScOptionsWalletsList>
                <div className="list">
                  {optionsWallets.map(({ value, name, icon }) => (
                    <CurrencyItem
                      key={value}
                      currencyCode={value}
                      icon={icon}
                      label={name}
                      noAction
                      isActive
                    />
                  ))}
                </div>
                {!noEditCurrencyBtn && (
                  <EditCurrenciesButton
                    large
                    mode={props.mode}
                    onClick={() => setIsEditCurrenciesOpen(true)}
                  />
                )}
              </ScOptionsWalletsList>
            </ModalSelectContainer>
          ) : (
            <ModalSelect
              icon={<images.currencyIcon.component />}
              title={t('currency')}
              options={extendedUserWallets.map(
                ({ currency, amountCents, subCurrency }) => ({
                  name: `${amountCents} ${subCurrency}`,
                  value: currency,
                })
              )}
              cols={1}
              onChange={(value) => {
                handleChange(value as Wallet['currency']);
              }}
              onClose={handleOpen}
              openOptionsInPortal
              innerAlignBottom
            />
          )
        ) : (
          <DepositOptions
            ref={ref}
            deposits={extendedUserWallets}
            handleChange={handleChange}
            isInputAddon={isInputAddon}
            mode={props.mode}
            noEditCurrencyBtn={noEditCurrencyBtn}
            onClick={() => setIsEditCurrenciesOpen(true)}
          />
        ))}
      {isEditCurrenciesOpen && (
        <EditCurrenciesModal onClose={() => setIsEditCurrenciesOpen(false)} />
      )}
    </ScSelectDeposit>
  );
};

type WalletOptions = Pick<Wallet, 'amountCents' | 'currency'> & {
  subCurrency: string;
};

interface DepositOptionsProps {
  deposits: WalletOptions[];
  handleChange: (currency: Wallet['currency']) => void;
  isInputAddon?: boolean;
  mode?: ThemeMode;
  noEditCurrencyBtn?: boolean;
  onClick: () => void;
}

const DepositOptions = forwardRef<HTMLDivElement, DepositOptionsProps>(
  (
    { deposits, handleChange, isInputAddon, mode, noEditCurrencyBtn, onClick },
    ref
  ) => {
    const { images } = useTheme();
    const user = useCommonStore.use.user();

    const maxBlockHeight = useMemo(() => {
      const totalHeight = deposits.length * 40 - 16;
      const max = isInputAddon ? 144 : 180;

      return `${totalHeight > max ? max : totalHeight}px`;
    }, [deposits.length, isInputAddon]);

    return (
      <div ref={ref} className="options">
        <ScrollBlock height={maxBlockHeight} scrollTopMargin={0} mode={mode}>
          <ul>
            {deposits.map(({ currency, amountCents, subCurrency }) => (
              <li
                key={currency}
                onClick={() => handleChange(currency)}
                className={
                  currency === user?.currency || deposits.length === 1
                    ? 'active'
                    : ''
                }
              >
                <>
                  {amountWithCurrencySymbol(
                    getCurrencyName(subCurrency),
                    amountCents
                  )}
                  {((isInputAddon && currency === user?.currency) ||
                    deposits.length === 1) && (
                    <images.tickCircleIcon.component />
                  )}
                </>
              </li>
            ))}
          </ul>
        </ScrollBlock>

        {!noEditCurrencyBtn && isInputAddon && (
          <EditCurrenciesButton mode={mode} onClick={onClick} />
        )}
      </div>
    );
  }
);

const EditCurrenciesButton: FC<{
  large?: boolean;
  mode?: ThemeMode;
  onClick: () => void;
}> = ({ large, mode, onClick }) => {
  const { t } = useTranslation('deposits');
  const isMobileDevice = useCommonStore.use.isMobileDevice();

  return (
    <Button
      type="button"
      blackTransparent={!isMobileDevice && mode !== 'dark'}
      whiteTransparent={isMobileDevice || mode === 'dark'}
      autoPaddingHorizontal
      wide
      small={!large}
      large={large}
      onClick={onClick}
    >
      {t('editCurrency.title')}
    </Button>
  );
};
